import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// EN
import enAsm from '../locales/en/asm.json';
import enCommon from '../locales/en/common.json';
import enCompany from '../locales/en/company.json';
import enCookies from '../locales/en/cookies.json';
import enDashboard from '../locales/en/dashboard.json';
import enGapAnalysis from '../locales/en/gap-analysis.json';
import enMapping from '../locales/en/mapping.json';
import enPolicy from '../locales/en/policy.json';
import enTransparency from '../locales/en/transparency.json';
import enUser from '../locales/en/user.json';
import enVendor from '../locales/en/vendor.json';

// PT
import ptAsm from '../locales/pt/asm.json';
import ptCommon from '../locales/pt/common.json';
import ptCompany from '../locales/pt/company.json';
import ptCookies from '../locales/pt/cookies.json';
import ptDashboard from '../locales/pt/dashboard.json';
import ptGapAnalysis from '../locales/pt/gap-analysis.json';
import ptMapping from '../locales/pt/mapping.json';
import ptPolicy from '../locales/pt/policy.json';
import ptTransparency from '../locales/pt/transparency.json';
import ptUser from '../locales/pt/user.json';
import ptVendor from '../locales/pt/vendor.json';

// ES
import esAsm from '../locales/es/asm.json';
import esCommon from '../locales/es/common.json';
import esCompany from '../locales/es/company.json';
import esCookies from '../locales/es/cookies.json';
import esDashboard from '../locales/es/dashboard.json';
import esGapAnalysis from '../locales/es/gap-analysis.json';
import esMapping from '../locales/es/mapping.json';
import esPolicy from '../locales/es/policy.json';
import esTransparency from '../locales/es/transparency.json';
import esUser from '../locales/es/user.json';
import esVendor from '../locales/es/vendor.json';

const LANG_MAP = { pt: 'pt-BR', en: 'en', es: 'es' };
const SUPPORTED_LANGS = ['pt', 'en', 'es'];

// Custom detector: reads company default language from persisted Redux state (AC #1 fallback chain)
const companyDefaultDetector = {
  name: 'companyDefault',
  lookup() {
    try {
      const persistRoot = localStorage.getItem('persist:root');
      if (!persistRoot) return undefined;
      const parsed = JSON.parse(persistRoot);
      if (!parsed.auth) return undefined;
      const authData = JSON.parse(parsed.auth);
      const companyLang = authData?.tenant?.defaultLanguage;
      return (companyLang && SUPPORTED_LANGS.includes(companyLang)) ? companyLang : undefined;
    } catch (e) {
      return undefined;
    }
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(companyDefaultDetector);

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: SUPPORTED_LANGS,
    nonExplicitSupportedLngs: true, // pt-BR → pt, es-419 → es
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: ['common', 'asm', 'gap-analysis', 'vendor', 'policy', 'mapping', 'dashboard', 'company', 'user', 'cookies', 'transparency'],

    resources: {
      en: {
        asm: enAsm,
        common: enCommon,
        company: enCompany,
        cookies: enCookies,
        dashboard: enDashboard,
        'gap-analysis': enGapAnalysis,
        mapping: enMapping,
        policy: enPolicy,
        transparency: enTransparency,
        user: enUser,
        vendor: enVendor,
      },
      pt: {
        asm: ptAsm,
        common: ptCommon,
        company: ptCompany,
        cookies: ptCookies,
        dashboard: ptDashboard,
        'gap-analysis': ptGapAnalysis,
        mapping: ptMapping,
        policy: ptPolicy,
        transparency: ptTransparency,
        user: ptUser,
        vendor: ptVendor,
      },
      es: {
        asm: esAsm,
        common: esCommon,
        company: esCompany,
        cookies: esCookies,
        dashboard: esDashboard,
        'gap-analysis': esGapAnalysis,
        mapping: esMapping,
        policy: esPolicy,
        transparency: esTransparency,
        user: esUser,
        vendor: esVendor,
      },
    },

    detection: {
      order: ['localStorage', 'navigator', 'companyDefault'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
      convertDetectedLanguage: (lng) => {
        // Strip region: pt-BR → pt, es-419 → es
        // Return undefined for unsupported languages so next detector in chain is tried
        const base = lng.split('-')[0].toLowerCase();
        return SUPPORTED_LANGS.includes(base) ? base : undefined;
      },
    },

    interpolation: {
      escapeValue: true, // XSS protection — DO NOT set to false
    },

    react: {
      useSuspense: true,
    },
  });

// Update HTML lang attribute on language change (Task 6.1)
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = LANG_MAP[lng] || lng;
});

// Set initial HTML lang attribute after i18next init resolves (Task 6.2)
i18n.isInitialized
  ? (document.documentElement.lang = LANG_MAP[i18n.language] || i18n.language)
  : i18n.on('initialized', () => {
      document.documentElement.lang = LANG_MAP[i18n.language] || i18n.language;
    });

export default i18n;
